import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  FaMicrophone,
  FaGlobe,
  FaChalkboardTeacher,
  FaUsers,
} from "react-icons/fa";

const EventStats = () => {
  const stats = [
    {
      title: "Total Talks",
      count: 30,
      description: "Engaging sessions delivered worldwide.",
      icon: <FaMicrophone className="text-3xl" />,
      color: "from-blue-400 to-cyan-400",
    },
    {
      title: "Countries",
      count: 15,
      description: "Countries visited for global insights.",
      icon: <FaGlobe className="text-3xl" />,
      color: "from-purple-400 to-pink-400",
    },
    {
      title: "Workshops & Trainings",
      count: 12,
      description: "Hands-on sessions for practical learning.",
      icon: <FaChalkboardTeacher className="text-3xl" />,
      color: "from-green-400 to-emerald-400",
    },
    {
      title: "Meetups & Conferences",
      count: 18,
      description: "Networking & knowledge-sharing events.",
      icon: <FaUsers className="text-3xl" />,
      color: "from-orange-400 to-red-400",
    },
  ];

  const sectionRef = useRef(null);
  const [counters, setCounters] = useState(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          stats.forEach((stat, index) => {
            let start = 0;
            const end = stat.count;
            const duration = 2000;
            const increment = end / (duration / 16);

            const timer = setInterval(() => {
              start += increment;
              if (start >= end) {
                start = end;
                clearInterval(timer);
              }
              setCounters((prev) => {
                const newCounters = [...prev];
                newCounters[index] = Math.floor(start);
                return newCounters;
              });
            }, 16);
          });
        }
      },
      { threshold: 0.5 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <motion.section
      className="relative py-16 bg-gradient-to-b from-gray-800 via-gray-900 to-gray-800 rounded-2xl mb-8 overflow-hidden"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
      ref={sectionRef}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 bg-hero-pattern opacity-5"></div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.h2
          className="text-4xl sm:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Event Statistics
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                {/* Icon */}
                <motion.div
                  className={`inline-flex p-4 rounded-full bg-gradient-to-r ${stat.color} bg-opacity-20 mb-4`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <div
                    className={`bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}
                  >
                    {stat.icon}
                  </div>
                </motion.div>

                {/* Counter */}
                <h3
                  className={`text-5xl font-bold mb-2 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}
                >
                  {counters[index]}+
                </h3>

                {/* Title */}
                <p className="text-lg font-semibold text-white mb-2">
                  {stat.title}
                </p>

                {/* Description */}
                <p className="text-sm text-gray-400">{stat.description}</p>

                {/* Hover effect line */}
                <div
                  className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-xl`}
                ></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default EventStats;
